import { useState, useRef, useEffect, useMemo } from 'react';
import { MathJaxContext } from 'better-react-mathjax';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { 
  ArrowCounterclockwise, 
  Robot, 
  ChevronDown, 
  BookHalf, 
  ClockHistory, 
  Check,
  BookmarkCheckFill,
  Clock,
  Images
} from 'react-bootstrap-icons';
import LessonSidebar from '../components/LessonSidebar';
import useTtsControls from '../hooks/useTtsControls';
import useSessionHistory from '../hooks/useSessionHistory';
import Header from '../components/layout/Header';
import { useTutorSession } from '../hooks/useTutorSession';
import { useBedrockClient } from '../context/BedrockClientContext';
import { useDynamoUtils } from '../context/DynamoUtilsContext';
import { useSession } from '../context/SessionContext';
import { usePersistentSession } from '../hooks/usePersistentSession';
import { ChatMessages } from '../components/ChatMessages';
import ChatInput from '../components/ChatInput';
import SuggestedResponses from '../components/SuggestedResponses';
import { getUserProfile } from '../utils/profileUtils';
import { fetchLessonBlockFromS3 } from '../utils/S3Utils';
import { generateSuggestedResponses as generateOpenAISuggestions } from '../utils/openAIService';

import {
  CURRICULUM_MAP,
  GET_THE_BASICS_SYSTEM_PROMPT,
  REVISION_SYSTEM_PROMPT,
  BECOME_AN_EXPERT_SYSTEM_PROMPT
} from '../config';

// Import the redesigned CSS
import '../assets/styles/TutorSessionRedesign.css';
import '../assets/styles/ChapterStyles.css';

const AI_PROVIDER = process.env.REACT_APP_AI_PROVIDER || 'openai';

const TutorSession = () => {
  // Get URL parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionIdFromUrl = queryParams.get('session');
  const activeOptionFromUrl = queryParams.get('activeOption');
  
  // Auth & user context
  const auth = useAuth();
  const { user } = auth || {};
  const userId = user?.sub || null;
  const bedrockClientRef = useBedrockClient();
  const dynamoUtilsRef = useDynamoUtils();
  const { setSessionId: setGlobalSessionId } = useSession();
  
  // References for scrolling and components
  const messagesEndRef = useRef(null);

  // Profile and initialization states
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [userProfile, setUserProfile] = useState(() => {
    const storedProfile = localStorage.getItem('userProfile');
    return storedProfile ? JSON.parse(storedProfile) : null;
  });
  const [isAuthReady, setIsAuthReady] = useState(!!userId);
  
  // Suggested responses feature
  const [suggestedResponses, setSuggestedResponses] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(() => {
    const savedPref = localStorage.getItem('showSuggestions');
    return savedPref !== null ? JSON.parse(savedPref) : true;
  });
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  
  // Text-to-speech controls
  const { enableTts, toggleTts, ttsState, ttsRef, TtsStreamerElement } = useTtsControls(userId);
  
  const chatInputRef = useRef(null);
  
  // Sidebar and main content states
  const [activeOption, setActiveOption] = useState('newLesson'); // 'newLesson' or 'lessonHistory'
  const [inFocusedMode, setInFocusedMode] = useState(false);
  
  // Lesson content states
  const [currentLessonBlockData, setCurrentLessonBlockData] = useState(null);
  const [showLessonPanels, setShowLessonPanels] = useState(true); // Default to showing panels
  
  // Session states
  const [selectedModule, setSelectedModule] = useState('');
  const [moduleDetails, setModuleDetails] = useState(null);
  const [difficulty, setDifficulty] = useState('');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [resumeSession, setResumeSession] = useState(null);
  
  // Mobile device detection (not responsive design)
  const [isMobile, setIsMobile] = useState(false);
  
  // Chapter accordion state
  const [expandedChapters, setExpandedChapters] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const sessionsPerPage = 10;
  
  // Track if lesson block is currently loading
  const [isLoadingLessonBlock, setIsLoadingLessonBlock] = useState(false);

  const [ttsLimitInfo, setTtsLimitInfo] = useState(null);

  // Tutor session hook
  const {
    messages,
    sessionId,
    isStreaming,
    isInitializing,
    createNewConversation,
    loadConversation,
    sendMessage,
    stopGeneration,
    markSessionCompleted,
    handleDrawPadSend,
    handleFileUpload,
    setMessages,
    setSessionId,
    ttsLimitReached,
    chatLimitReached,
    chatLimitInfo
  } = useTutorSession({
    auth,
    userProfile,
    customTableName: 'ai-math-tutor-conversations',
    sessionType: 'Tutor Chat',
    ttsRef,
    enableTts,
    onTtsLimitReached: (limitInfo) => {
      console.log('TTS Limit callback received:', limitInfo);
      setTtsLimitInfo(limitInfo);
    }
  });

  // Functions for suggested responses
  const fetchSuggestedResponses = async () => {
    if (!showSuggestions || isFetchingSuggestions || messages.length < 2) {
      return;
    }

    setIsFetchingSuggestions(true);

    try {
      const recentMessages = [...messages].slice(-4);

      const lastMessage = recentMessages[recentMessages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant') {
        if (AI_PROVIDER === 'bedrock') {
          const bedrockClient = bedrockClientRef.current;
          if (bedrockClient) {
            const suggestions = await bedrockClient.generateSuggestedResponses(recentMessages, userId);
            setSuggestedResponses(suggestions);
          }
        } else {
          let tokenToUse = auth.user?.tokens?.idToken || null;
          if (!tokenToUse && typeof auth.getIdToken === 'function') {
            tokenToUse = await auth.getIdToken();
          }
          if (!tokenToUse) {
            console.error('OpenAI Auth token not available.');
            return;
          }
          const suggestions = await generateOpenAISuggestions({
            token: tokenToUse,
            recentMessages,
            userId,
            dynamoUtils: dynamoUtilsRef.current,
          });
          setSuggestedResponses(suggestions);
        }
      }
    } catch (error) {
      console.error('Error fetching suggested responses:', error);
    } finally {
      setIsFetchingSuggestions(false);
    }
  };

  // Handle suggestion clicks
  const handleSuggestionClick = (suggestion) => {
    if (chatInputRef.current) {
      chatInputRef.current.setValue(suggestion);
      chatInputRef.current.focus();
    }
    setSuggestedResponses([]); // Clear suggestions after clicking
  };

  // Wrapper for sendMessage to clear suggestions
  const handleSendWithSuggestionsClear = (text, systemPrompt) => {
    setSuggestedResponses([]); // Clear suggestions
    sendMessage(text, systemPrompt);
  };

  // Save user preference for showing suggestions
  useEffect(() => {
    localStorage.setItem('showSuggestions', JSON.stringify(showSuggestions));
  }, [showSuggestions]);


  // Generate suggestions when assistant finishes responding
  useEffect(() => {
    if (!isStreaming && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && !lastMessage.status) {
        fetchSuggestedResponses();
      }
    }
  }, [isStreaming, messages]);

  // Update global session context
  useEffect(() => {
    if (sessionId) {
      setGlobalSessionId(sessionId);
    }
  }, [sessionId, setGlobalSessionId]);

  // Session progress hook
  const {
    sessionProgress,
    isLoadingProgress,
    updateSessionProgress
  } = usePersistentSession(userProfile);

  // Available modules state
  const [availableModules, setAvailableModules] = useState([]);
  
  // Track if we need to load a session from URL
  const needToLoadFromUrlRef = useRef(!!sessionIdFromUrl);

  // Initialize currentLessonBlock with EXAMPLE_LESSON_BLOCK when component mounts
  useEffect(() => {
    if (!currentLessonBlockData) {
      setCurrentLessonBlockData(null);
    }
  }, [currentLessonBlockData]);

  // Check for actual mobile device (not just screen size)
  useEffect(() => {
    const isMobileDevice = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };
    
    // Set mobile state based on device detection, not screen size
    setIsMobile(isMobileDevice());
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Monitor auth status
  useEffect(() => {
    if (userId && !isAuthReady) {
      setIsAuthReady(true);
    }
  }, [userId, isAuthReady]);

  // Fetch the user profile
  useEffect(() => {
    async function fetchProfile() {
      setIsLoadingProfile(true);
      try {

        // If we have a user ID from auth, use it for profile lookup
        const profileOptions = userId ? { userId } : {};
        const profile = await getUserProfile(profileOptions);
        
        if (profile) {
          setUserProfile(profile);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    }
    
    fetchProfile();
  }, [userId, auth]);

  // Load available modules based on user profile
  useEffect(() => {
    if (userProfile) {
      const state = userProfile.state || 'NSW'; // Get state from profile, default to NSW
      const textbook = userProfile.textbook || 'cambridge';
      const yearLevel = userProfile.yearLevel || '11';
      const mathsType = userProfile.mathsType || 'standard';
      const curriculumKey = `${yearLevel}_${mathsType}`;
      
      // Check if we have modules for this specific curriculum under the selected state
      if (CURRICULUM_MAP[state] && 
          CURRICULUM_MAP[state][textbook] && 
          CURRICULUM_MAP[state][textbook][curriculumKey]) {
        // Filter to only include modules with type "content"
        const contentModules = CURRICULUM_MAP[state][textbook][curriculumKey].filter(module => module.type === 'content');
        setAvailableModules(contentModules);
      } else if (CURRICULUM_MAP[state] && CURRICULUM_MAP[state][textbook]) {
        // Fallback to all modules for this textbook
        const firstKey = Object.keys(CURRICULUM_MAP[state][textbook])[0];
        const allModules = CURRICULUM_MAP[state][textbook][firstKey] || [];
        // Filter to only include modules with type "content"
        const contentModules = allModules.filter(module => module.type === 'content');
        setAvailableModules(contentModules);
      } else {
        // Last resort fallback
        setAvailableModules([]);
      }
    }
  }, [userProfile]);

  // Organize modules by chapter
  const modulesByChapter = useMemo(() => {
    // Group modules by chapter
    if (!availableModules || availableModules.length === 0) return {};
    
    return availableModules.reduce((chapters, module) => {
      const chapter = module.chapter;
      if (!chapters[chapter]) {
        chapters[chapter] = [];
      }
      chapters[chapter].push(module);
      return chapters;
    }, {});
  }, [availableModules]);

  // Sort chapters numerically
  const sortedChapters = useMemo(() => {
    return Object.keys(modulesByChapter).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      return numA - numB;
    });
  }, [modulesByChapter]);

  // Watch for changes in isInitializing to know when authentication is ready
  useEffect(() => {
    // Only attempt to load if:
    // 1. We have a session ID from URL
    // 2. Auth is ready
    // 3. Not currently initializing
    // 4. We haven't already started a session
    // 5. We haven't handled this URL session yet
    if (sessionIdFromUrl && 
        !isInitializing && 
        isAuthReady && 
        !sessionStarted && 
        needToLoadFromUrlRef.current) {
      
      needToLoadFromUrlRef.current = false; // Mark as handled
      
      console.log('Auth is ready, loading session from URL:', sessionIdFromUrl);
      loadSessionFromUrl(sessionIdFromUrl);
    }
  }, [sessionIdFromUrl, isAuthReady, isInitializing, sessionStarted]);

  // Set initial active option based on URL parameter
  useEffect(() => {
    if (activeOptionFromUrl === 'lessonHistory') {
      setActiveOption('lessonHistory');
      fetchSessionHistory(); // Load the history data immediately
    }
  }, [activeOptionFromUrl]);

  const { sessionHistory, fetchSessionHistory, isLoadingHistory } = useSessionHistory(userId);

  // Load session from URL parameter
  async function loadSessionFromUrl(sessionIdFromUrl) {
    if (!sessionIdFromUrl || !userId) return;
    
    try {
      console.log('Loading session from URL:', sessionIdFromUrl);
      const sessionData = await loadConversation(sessionIdFromUrl);
      
      if (sessionData && sessionData.metadata) {
        const { moduleId, difficulty: sessionDifficulty, chapter, topic } = sessionData.metadata;
        
        // Set difficulty from session metadata if available
        if (sessionDifficulty) {
          setDifficulty(sessionDifficulty);
        }
        
        // Find module details if module ID exists
        if (moduleId && availableModules.length > 0) {
          const foundModule = availableModules.find(mod => mod.id === moduleId);
          if (foundModule) {
            console.log('Found module details from session metadata:', foundModule);
            setModuleDetails(foundModule);
            setSelectedModule(moduleId);
            
            // Fetch the lesson block data for this module
            setIsLoadingLessonBlock(true);
            try {
              const lessonBlock = await fetchLessonBlockFromS3(moduleId, userProfile);
              if (lessonBlock) {
                console.log('Loaded lesson block for continued session:', moduleId);
                setCurrentLessonBlockData(lessonBlock);
              } else {
                console.error('Could not load lesson block for continued session:', moduleId);
              }
            } catch (error) {
              console.error('Error loading lesson block for continued session:', error);
            } finally {
              setIsLoadingLessonBlock(false);
            }
          }
        }
      }
      
      setSessionStarted(true);
      setInFocusedMode(true);
    } catch (error) {
      console.error('Error loading session from URL:', error);
    }
  }

  // If user picks a session in the sidebar, load it
  async function handleSelectSession(convoId) {
    try {
      const sessionData = await loadConversation(convoId);
      
      if (sessionData && sessionData.metadata) {
        const { moduleId, difficulty: sessionDifficulty, chapter, topic } = sessionData.metadata;
        
        // Set difficulty from session metadata if available
        if (sessionDifficulty) {
          setDifficulty(sessionDifficulty);
        }
        
        // Find module details if module ID exists
        if (moduleId && availableModules.length > 0) {
          const foundModule = availableModules.find(mod => mod.id === moduleId);
          if (foundModule) {
            console.log('Found module details from session metadata:', foundModule);
            setModuleDetails(foundModule);
            setSelectedModule(moduleId);
            
            // Fetch the lesson block data for this module
            setIsLoadingLessonBlock(true);
            try {
              const lessonBlock = await fetchLessonBlockFromS3(moduleId, userProfile);
              if (lessonBlock) {
                console.log('Loaded lesson block for continued session:', moduleId);
                setCurrentLessonBlockData(lessonBlock);
              } else {
                console.error('Could not load lesson block for continued session:', moduleId);
              }
            } catch (error) {
              console.error('Error loading lesson block for continued session:', error);
            } finally {
              setIsLoadingLessonBlock(false);
            }
          }
        }
      }
      
      setSessionStarted(true);
      setInFocusedMode(true);
    } catch (error) {
      console.error('Error loading session:', error);
    }
  }

  // Function to generate dynamic system prompt directly from a lesson block (not from state)
  const generateDynamicSystemPrompt = (difficultyLevel, lessonBlock) => {
    if (!lessonBlock) {
      console.error('No lesson block data provided to generateDynamicSystemPrompt');
      return "No lesson block data available.";
    }
    
    console.log('Generating system prompt with lesson block for difficulty:', difficultyLevel);
    
    // Get actual year and math type from user profile
    const year = userProfile?.yearLevel || '11';
    const type = userProfile?.mathsType ? formatMathType(userProfile.mathsType) : 'Standard';
    
    // Select the appropriate system prompt based on difficulty level
    let basePrompt;
    switch (difficultyLevel) {
      case 'easy':
        basePrompt = GET_THE_BASICS_SYSTEM_PROMPT({ lessonBlock, userProfile, year, type });
        break;
      case 'medium':
        basePrompt = REVISION_SYSTEM_PROMPT({ lessonBlock, userProfile, year, type });
        break;
      case 'hard':
        basePrompt = BECOME_AN_EXPERT_SYSTEM_PROMPT({ lessonBlock, userProfile, year, type });
        break;
      default:
        basePrompt = REVISION_SYSTEM_PROMPT({ lessonBlock, userProfile, year, type });
    }
    
    return basePrompt;
  };

  // Handle starting a new session with selected module and difficulty
  async function handleStartSession(moduleId, difficultyLevel) {
    if (!moduleId || !difficultyLevel) {
      alert('Please select a module and difficulty first!');
      return;
    }

    // Get the selected module details from the available modules
    const moduleInfo = availableModules.find(mod => mod.id === moduleId);
    setModuleDetails(moduleInfo);
    
    // Update state to reflect chosen options
    setSelectedModule(moduleId);
    setDifficulty(difficultyLevel);
    
    // Set loading state while fetching lesson block
    setIsLoadingLessonBlock(true);
    
    // Try to fetch the lesson block from S3
    let lessonBlock = null;
    try {
      lessonBlock = await fetchLessonBlockFromS3(moduleId, userProfile);
      
      // If the fetch was not successful, show an error and don't start session
      if (!lessonBlock) {
        setIsLoadingLessonBlock(false);
        alert(`Lesson couldn't be found. Please try another lesson or contact support.`);
        return;
      }
      
      console.log('Lesson block loaded from S3:', moduleId);
      
      // Update the state with the lesson block
      setCurrentLessonBlockData(lessonBlock);
      
      // Create an initialization message that goes to the LLM but not shown to the user
      // get current time in australian eastern time
      const currentTime = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Sydney' });
      const meta_user_message = `
USER:
  - Name: ${userProfile?.name || 'Student'}
  - Year: ${userProfile?.yearLevel || '11'} ${userProfile?.mathsType || 'Standard'}
  - Textbook: ${userProfile?.textbook || 'Cambridge'}
  - State: ${userProfile?.state || 'NSW'}
  - Confidence Level: ${userProfile?.confidenceLevel || 'medium'}
  - Interests: ${userProfile?.interests || 'N/A'}
Time: ${currentTime}
`.trim();

      // Create comprehensive metadata for progress tracking
      const meta = { 
        moduleId: moduleId, // Store the actual module ID for tracking
        id: moduleId, // For backward compatibility 
        topic: moduleInfo?.name || moduleId,
        difficulty: difficultyLevel,
        textbook: userProfile?.textbook || 'cambridge',
        yearLevel: userProfile?.yearLevel || '11',
        mathLevel: userProfile?.mathsType || 'standard',
        chapter: moduleInfo?.chapter || '',
        section: moduleInfo?.section || '',
        completed: false,
        initialMessage: true
      };
      
      // Enter focused mode and mark session as started
      setInFocusedMode(true);
      setSessionStarted(true);

      // Get the dynamic system prompt directly using the lesson block rather than from state
      const systemPrompt = generateDynamicSystemPrompt(difficultyLevel, lessonBlock);
      
      // Create conversation and automatically start streaming the tutor's first message
      try {

        // Use the initializeLesson function from the hook
        await sendMessage(meta_user_message, systemPrompt, meta);
        
        // Update the session progress tracking
        updateSessionProgress(moduleId, difficultyLevel, sessionId, false);
      } catch (error) {
        console.error("Error starting the lesson:", error);
        // Let the user know something went wrong
        alert("There was an error starting this lesson. Please try again.");
        setSessionStarted(false);
        setInFocusedMode(false);
      }
    } catch (error) {
      console.error('Error fetching lesson block from S3:', error);
      setIsLoadingLessonBlock(false);
      alert(`Lesson couldn't be found. Please try another lesson or contact support. Error: ${error.message}`);
      return;
    } finally {
      setIsLoadingLessonBlock(false);
    }
  }

  // Handle finishing a session with confirmation
  const handleFinishSession = () => {
    setShowFinishConfirm(true);
  };
  
  // Actually complete the session after confirmation
  const confirmFinishSession = async () => {
    if (!sessionId) return;
    
    try {
      await markSessionCompleted(sessionId);
      // Update progress in our local state too
      if (selectedModule) {
        updateSessionProgress(selectedModule, difficulty, sessionId, true);
      }
      
      // Hide the confirmation modal
      setShowFinishConfirm(false);
      
      // Reset session
      handleReset();
    } catch (error) {
      console.error("Error marking session as completed:", error);
      alert("There was an error completing the session. Please try again.");
    }
  };
  
  // Handle pausing a session (return to tutor page without marking as complete)
  const handlePauseSession = () => {
    setInFocusedMode(false);
    setSessionStarted(false);
    setSelectedModule('');
    setModuleDetails(null);
    setDifficulty('');
    setShowLessonPanels(true);
    
    // Clear the session from the hook
    setMessages([]);
    setSessionId(null);
    
    // Clear any lesson block data
    setCurrentLessonBlockData(null);
    
    // Refresh session history to show updated state
    fetchSessionHistory();
  };

  // Reset page function
  const handleReset = () => {
    // Reset UI state
    setSessionStarted(false);
    setSelectedModule('');
    setModuleDetails(null);
    setDifficulty('');
    setShowLessonPanels(true);
    setInFocusedMode(false);
    
    // Clear the messages without creating a new conversation in DynamoDB
    setMessages([]);
    
    // Reset session ID to null so a new one will be created on the next interaction
    setSessionId(null);
    
    // Refresh session history
    fetchSessionHistory();
  };

  // Toggle chapter expand/collapse
  const toggleChapter = (chapter) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapter]: !prev[chapter]
    }));
  };

  // Toggle lesson panels
  const toggleLessonPanels = () => {
    setShowLessonPanels(!showLessonPanels);
  };

  // Format the textbook name for display (capitalize first letter)
  const formatTextbookName = (textbookName) => {
    if (!textbookName) return '';
    return textbookName.charAt(0).toUpperCase() + textbookName.slice(1);
  };

  const getChapterDisplayName = (chapterObj) =>
    `Chapter ${chapterObj.chapter}: ${chapterObj.name}`;
  

  // Format math type for display (capitalize first letter)
  const formatMathType = (mathType) => {
    if (!mathType) return '';
    return mathType.charAt(0).toUpperCase() + mathType.slice(1);
  };

  // Format natural date time
  function formatNaturalDateTime(ts) {
    return new Date(ts).toLocaleString('en-US', {
      weekday: 'short',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Get difficulty label
  const getDifficultyLabel = (diffLevel) => {
    switch(diffLevel) {
      case 'easy': return 'Get the Basics';
      case 'medium': return 'Revision';
      case 'hard': return 'Become an Expert';
      default: return diffLevel.charAt(0).toUpperCase() + diffLevel.slice(1);
    }
  };

  return (
    <MathJaxContext>
      <div className="tutor-session-page">
        {/* Mobile Access Restriction */}
        {isMobile ? (
          <div className="mobile-restriction-overlay">
            <Header 
              inFocusedMode={false}
              moduleDetails={null}
              difficulty=""
              onPauseSession={() => {}}
              onFinishSession={() => {}}
              getDifficultyLabel={() => ""}
            />
            <div className="mobile-restriction-content">
              <div className="mobile-restriction-card">
                <div className="mobile-restriction-icon">
                  <BookHalf size={48} />
                </div>
                <h2>Desktop Only Feature</h2>
                <p>
                  The interactive tutoring session is designed for desktop and tablet devices 
                  to provide the best learning experience with our advanced tools and interface.
                </p>
                <div className="mobile-alternative">
                  <h3>Need help with homework?</h3>
                  <p>
                    Our Homework Help feature is fully optimized for mobile devices! 
                    You can upload photos of your math problems and get step-by-step guidance.
                  </p>
                  <Link to="/homework-help" className="homework-help-btn">
                    <Images className="btn-icon" />
                    Go to Homework Help
                  </Link>
                </div>
                <div className="mobile-tip">
                  <small>
                    💡 Tip: Access this page from a computer or tablet for the full tutoring experience
                  </small>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <Header 
              inFocusedMode={inFocusedMode}
              moduleDetails={moduleDetails}
              difficulty={difficulty}
              onPauseSession={handlePauseSession}
              onFinishSession={handleFinishSession}
              getDifficultyLabel={getDifficultyLabel}
            />
            
            <div className="tutor-content">
              {/* Left Sidebar with simple option bubbles - hide when in focused mode */}
              {!inFocusedMode && (
                <div className="tutor-sidebar">            
                  {/* Option Bubbles */}
                  <div className="sidebar-options">
                    <div 
                      className={`sidebar-option ${activeOption === 'newLesson' ? 'active' : ''}`}
                      onClick={() => setActiveOption('newLesson')}
                    >
                      <BookHalf className="option-icon" />
                      <div className="option-title">New Lesson</div>
                      <div className="option-description">
                        Start a new tutoring lesson from your curriculum
                      </div>
                    </div>
                    
                    <div 
                      className={`sidebar-option ${activeOption === 'lessonHistory' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveOption('lessonHistory');
                        fetchSessionHistory(); // Refresh history when selected
                      }}
                    >
                      <ClockHistory className="option-icon" />
                      <div className="option-title">Lesson History</div>
                      <div className="option-description">
                        Continue from your previous lessons
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content Area */}
              <div className="tutor-main-area">
                {/* Content based on selected sidebar option */}
                {activeOption === 'newLesson' && (
                  <div className="chapters-container">                
                    {isLoadingProfile ? (
                      <div className="loading-indicator">
                        <div className="spinner"></div>
                        <p>Loading your curriculum...</p>
                      </div>
                    ) : !userProfile ? (
                      <div className="profile-warning">
                        <p>Please set up your profile to get personalized modules.</p>
                        <Link to="/setup" className="btn btn-primary">Complete Setup</Link>
                      </div>
                    ) : (
                      <div className="toc-sheet">
                        <h2 className="toc-title">
                          Year {userProfile?.yearLevel || '11'} Mathematics {formatMathType(userProfile?.mathsType) || 'Standard'} · {formatTextbookName(userProfile?.textbook)}
                        </h2>
                        
                        <div className="difficulty-explainer">
                          <div className="difficulty-type">
                            <div className="diff-icon-title">
                              <div className="diff-icon easy"></div>
                              <span className="diff-name">Get the Basics</span>
                            </div>
                            <span className="diff-desc">Core concepts and fundamentals with guided examples.</span>
                          </div>
                          <div className="difficulty-type">
                            <div className="diff-icon-title">
                              <div className="diff-icon medium"></div>
                              <span className="diff-name">Revision</span>
                            </div>
                            <span className="diff-desc">Reinforcement with more complex problems and applications.</span>
                          </div>
                          <div className="difficulty-type">
                            <div className="diff-icon-title">
                              <div className="diff-icon hard"></div>
                              <span className="diff-name">Expert</span>
                            </div>
                            <span className="diff-desc">Advanced concepts and challenging problem-solving.</span>
                          </div>
                        </div>

                        <div className="chapters-list">
                          {sortedChapters.map(chapter => (
                            <div 
                              key={chapter}
                              className={`chapter-accordion ${expandedChapters[chapter] ? 'expanded' : ''}`}
                            >
                              <div className="chapter-row" onClick={() => toggleChapter(chapter)}>
                                <span className="chap-num">Chapter {chapter}</span>
                                <span className="chap-separator">-</span>
                                <span className="chap-title">
                                  {modulesByChapter[chapter][0].name}
                                </span>
                                <span className="dot-leader" aria-hidden />
                                <ChevronDown
                                  className={expandedChapters[chapter] ? 'rotate-180' : ''}
                                />
                              </div>
                              
                              <div className="chapter-content">
                                <ul className="sub-list">
                                  {modulesByChapter[chapter].map((mod) => {
                                    const moduleProgress = sessionProgress?.[mod.id];

                                    return (
                                      <li key={mod.id} className="sub-item">
                                        <span>{mod.section || mod.name}</span>
                                        <div className="diff-chips">
                                          {/* EASY */}
                                          <button
                                            className={`difficulty-button easy ${
                                              moduleProgress?.easy?.completed ? 'completed' : 
                                              moduleProgress?.easy?.sessionId ? 'in-progress' : ''
                                            }`}
                                            onClick={() => {
                                              if (moduleProgress?.easy?.sessionId && !moduleProgress?.easy?.completed) {
                                                // Show modal for in-progress session
                                                setResumeSession({
                                                  moduleId: mod.id,
                                                  difficulty: 'easy',
                                                  sessionId: moduleProgress.easy.sessionId
                                                });
                                              } else {
                                                handleStartSession(mod.id, 'easy');
                                              }
                                            }}
                                          >
                                            <span className="difficulty-label">Get the Basics</span>
                                            {moduleProgress?.easy?.completed ? (
                                              <Check size={10} />
                                            ) : moduleProgress?.easy?.sessionId ? (
                                              <Clock size={10} />
                                            ) : null}
                                          </button>

                                          {/* MEDIUM */}
                                          <button
                                            className={`difficulty-button medium ${
                                              moduleProgress?.medium?.completed ? 'completed' : 
                                              moduleProgress?.medium?.sessionId ? 'in-progress' : ''
                                            }`}
                                            onClick={() => {
                                              if (moduleProgress?.medium?.sessionId && !moduleProgress?.medium?.completed) {
                                                // Show modal for in-progress session
                                                setResumeSession({
                                                  moduleId: mod.id,
                                                  difficulty: 'medium',
                                                  sessionId: moduleProgress.medium.sessionId
                                                });
                                              } else {
                                                handleStartSession(mod.id, 'medium');
                                              }
                                            }}
                                          >
                                            <span className="difficulty-label">Revision</span>
                                            {moduleProgress?.medium?.completed ? (
                                              <Check size={10} />
                                            ) : moduleProgress?.medium?.sessionId ? (
                                              <Clock size={10} />
                                            ) : null}
                                          </button>

                                          {/* HARD */}
                                          <button
                                            className={`difficulty-button hard ${
                                              moduleProgress?.hard?.completed ? 'completed' : 
                                              moduleProgress?.hard?.sessionId ? 'in-progress' : ''
                                            }`}
                                            onClick={() => {
                                              if (moduleProgress?.hard?.sessionId && !moduleProgress?.hard?.completed) {
                                                // Show modal for in-progress session
                                                setResumeSession({
                                                  moduleId: mod.id,
                                                  difficulty: 'hard',
                                                  sessionId: moduleProgress.hard.sessionId
                                                });
                                              } else {
                                                handleStartSession(mod.id, 'hard');
                                              }
                                            }}
                                          >
                                            <span className="difficulty-label">Expert</span>
                                            {moduleProgress?.hard?.completed ? (
                                              <Check size={10} />
                                            ) : moduleProgress?.hard?.sessionId ? (
                                              <Clock size={10} />
                                            ) : null}
                                          </button>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {activeOption === 'lessonHistory' && (
                  <div className="session-history-container">
                    <div className="history-header">
                      <h2 className="history-heading">Your Lesson History</h2>
                      <div className="history-actions">
                        <button 
                          className="refresh-history-btn"
                          onClick={fetchSessionHistory}
                        >
                          <ArrowCounterclockwise size={14} />
                          Refresh
                        </button>
                      </div>
                    </div>
                    
                    {isLoadingHistory ? (
                      <div className="loading-indicator">
                        <div className="spinner"></div>
                        <p>Loading your session history...</p>
                      </div>
                    ) : sessionHistory.length === 0 ? (
                      <div className="no-sessions">
                        <BookmarkCheckFill className="no-sessions-icon" />
                        <p>You haven't completed any lessons yet. Start a new lesson to begin learning!</p>
                      </div>
                    ) : (
                      <div>
                        <div className="history-dashboard">
                          <table>
                            <thead>
                              <tr>
                                <th>Lesson</th>
                                <th>Chapter & Section</th>
                                <th>Difficulty</th>
                                <th>Status</th>
                                <th>Last Activity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sessionHistory
                                .slice((currentPage - 1) * sessionsPerPage, currentPage * sessionsPerPage)
                                .map(session => {
                                const metadata = session.metadata || {};
                                const difficultyLabel = metadata.difficulty || 'medium';
                                const isCompleted = metadata.completed === true;
                                
                                return (
                                  <tr 
                                    key={session.conversation_id}
                                    onClick={() => handleSelectSession(session.conversation_id)}
                                  >
                                    <td>
                                      <div className="history-session-title">
                                        {metadata.topic || 'Math Tutoring Session'}
                                      </div>
                                    </td>
                                    <td>
                                      {metadata.chapter && (
                                        <div className="chapter-info">
                                          <span className="chapter-number">Chapter {metadata.chapter}</span>
                                          {metadata.section && (
                                            <span className="chapter-name">: {metadata.section}</span>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div className={`status-badge ${difficultyLabel}`}>
                                        {getDifficultyLabel(difficultyLabel)}
                                      </div>
                                    </td>
                                    <td>
                                      <div className={`status-badge ${isCompleted ? 'completed' : 'in-progress'}`}>
                                        {isCompleted ? (
                                          <>
                                            <Check size={12} />
                                            <span>Completed</span>
                                          </>
                                        ) : (
                                          <>
                                            <span>In Progress</span>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="session-date">
                                        {formatNaturalDateTime(session.timestamp)}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination for large history lists */}
                        {sessionHistory.length > sessionsPerPage && (
                          <div className="history-pagination">
                            {Array.from({ length: Math.ceil(sessionHistory.length / sessionsPerPage) }, (_, i) => i + 1).map(page => (
                              <button 
                                key={page}
                                className={`page-button ${currentPage === page ? 'active' : ''}`}
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Welcome screen shown when no sidebar option is selected */}
                {!activeOption && (
                  <div className="welcome-screen">
                    <Robot className="welcome-icon" />
                    <h1 className="welcome-title">Welcome to your AI Maths Tutor</h1>
                    <p className="welcome-subtitle">
                      Select "New Lesson" to start learning or "Lesson History" to continue where you left off.
                    </p>
                  </div>
                )}
                
                {/* Focused Tutor Mode (only shown when inFocusedMode is true) */}
                {inFocusedMode && (
                  <div className="focused-tutor-mode">
                    <LessonSidebar
                      visible={showLessonPanels}
                      onToggle={toggleLessonPanels}
                      lessonBlock={currentLessonBlockData}
                      difficulty={difficulty}
                    />
                    <div className="focused-content">
                      <div className="messages-container">
                        <ChatMessages 
                          messages={messages} 
                          /* TTS props removed from UI while keeping functionality intact
                          enableTts={enableTts}
                          ttsState={ttsState} */
                        />
                        <div ref={messagesEndRef} />
                      </div>
                      
                      <div className="chat-input-area">
                        {/* Add suggestions container */}
                        <div className="input-options-container">
                          <SuggestedResponses
                            suggestions={suggestedResponses}
                            onSuggestionClick={handleSuggestionClick}
                            visible={showSuggestions}
                          />
                        </div>
                        
                        <ChatInput
                          ref={chatInputRef}
                          isStreaming={isStreaming}
                          showSuggestions={showSuggestions}
                          onToggleSuggestions={() => setShowSuggestions(!showSuggestions)}
                          /* TTS props commented out - remove these comments to restore TTS UI
                          enableTts={enableTts}
                          ttsLimitReached={ttsLimitReached}
                          ttsLimitInfo={ttsLimitInfo}
                          onToggleTts={toggleTts}
                          */
                          chatLimitReached={chatLimitReached}
                          chatLimitInfo={chatLimitInfo}
                          sessionId={sessionId}
                          onSend={(text) => {
                            // Use the dynamic system prompt based on the current difficulty
                            const systemPrompt = generateDynamicSystemPrompt(difficulty || 'medium', currentLessonBlockData);
                            
                            // Log the user message and system prompt before sending to Bedrock
                            console.log('--- SENDING TO BEDROCK ---');
                            console.log('User message:');
                            console.log(text); // Log on separate line for better readability
                            console.log('System prompt:');
                            console.log(systemPrompt); // Log on separate line for better readability
                            
                            // Use the wrapper that clears suggestions
                            handleSendWithSuggestionsClear(text, systemPrompt);
                          }}
                          onFileUpload={handleFileUpload}
                          handleStopGeneration={stopGeneration}
                          disabled={!userId || isInitializing || chatLimitReached}
                          placeholder={
                            !userId
                              ? "You need to be logged in to use the tutor"
                              : isInitializing
                                ? "Initializing connection..."
                                : "Type your question here..."
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Finish Confirmation Modal */}
            {showFinishConfirm && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>Finish Tutoring Session</h3>
                    <button className="close-btn" onClick={() => setShowFinishConfirm(false)}>×</button>
                  </div>
                  <div className="modal-body">
                    <p>Are you sure you want to finish this tutoring session? This will mark it as completed.</p>
                    <div className="modal-actions">
                      <button 
                        className="cancel-btn"
                        onClick={() => setShowFinishConfirm(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        className="confirm-btn"
                        onClick={confirmFinishSession}
                      >
                        Yes, Finish Session
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Resume Session Modal */}
            {resumeSession && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>Resume Tutoring Session</h3>
                    <button className="close-btn" onClick={() => setResumeSession(null)}>×</button>
                  </div>
                  <div className="modal-body">
                    <p>
                      You have an in-progress session for this lesson. Would you like to resume it or start a new session?
                    </p>
                    <div className="modal-actions">
                      <button 
                        className="cancel-btn"
                        onClick={() => setResumeSession(null)}
                      >
                        Cancel
                      </button>
                      <button 
                        className="confirm-btn"
                        onClick={() => {
                          loadSessionFromUrl(resumeSession.sessionId);
                          setResumeSession(null);
                        }}
                      >
                        Resume Session
                      </button>
                      <button 
                        className="confirm-btn"
                        onClick={() => {
                          handleStartSession(resumeSession.moduleId, resumeSession.difficulty);
                          setResumeSession(null);
                        }}
                      >
                        Start New Session
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {TtsStreamerElement}
          </>
        )}
      </div>
    </MathJaxContext>
  );
};

export default TutorSession;